import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import {
  millisecondsIn,
  secondsIn,
  type DatabaseEntities,
  type Maybe,
  type YardTaskAttachment
} from '@nova/core';
import { documentKeys } from '@satellite/composables/queryKeys';
import documentService from '@satellite/services/document-service';

/** Lifetime of the presigned URLs; must outlive the TanStack `gcTime`. */
export const ATTACHMENT_URL_EXPIRATION_IN_SECONDS = secondsIn.hour;

const ATTACHMENT_URL_REFRESH_RATIO = 0.75;

const ATTACHMENTS_PAGE_SIZE = 100;

/** A PDF tile requests nothing until opened, so only a timer notices its URL going stale. */
function getUrlRefreshIntervalMs(expirationInSeconds: number): number {
  return expirationInSeconds * ATTACHMENT_URL_REFRESH_RATIO * millisecondsIn.second;
}

/** Mirrors the API's pagination envelope. */
export type AttachmentListMeta = {
  totalRecords: number;
  totalPages: number;
  page: number;
  limit: number;
  offset: number;
};

type AttachmentListResponse = { items: YardTaskAttachment[]; meta?: AttachmentListMeta };

export interface UseAttachmentListOptions {
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: MaybeRefOrGetter<DatabaseEntities>;
  /** The entity's id. The query stays disabled until there is one. */
  relatedId: MaybeRefOrGetter<Maybe<string>>;
  /** Further gate on top of the id check. */
  enabled?: MaybeRefOrGetter<boolean>;
  /** Page size. Read reactively, so a caller can resize without remounting. */
  limit?: MaybeRefOrGetter<number>;
  /** Page offset. Each page is cached separately; read `meta` to know when to stop. */
  offset?: MaybeRefOrGetter<number>;
  expirationInSeconds?: number;
}

/**
 * The documents attached to an entity, each carrying a presigned `url` the browser can use
 * directly — an `<img>` needs no CORS, which the bucket does not offer. Reads through its own
 * query, not an entity detail endpoint, so the URLs can be re-signed on their own schedule.
 */
export function useAttachmentList({
  relatedEntity,
  relatedId,
  enabled = true,
  limit = ATTACHMENTS_PAGE_SIZE,
  offset = 0,
  expirationInSeconds = ATTACHMENT_URL_EXPIRATION_IN_SECONDS
}: UseAttachmentListOptions) {
  const currentEntity = computed(() => toValue(relatedEntity));
  const currentId = computed(() => toValue(relatedId));
  const currentPage = computed(() => ({ limit: toValue(limit), offset: toValue(offset) }));

  const query = useQuery<AttachmentListResponse>({
    queryKey: computed(() =>
      documentKeys.list(currentEntity.value, currentId.value ?? '', currentPage.value)
    ),
    enabled: computed(() => Boolean(currentId.value) && toValue(enabled)),
    refetchInterval: getUrlRefreshIntervalMs(expirationInSeconds),
    // `refetch()` bypasses `enabled`, so the id is checked here as well.
    queryFn: ({ signal }) => {
      const id = currentId.value;
      if (!id) {
        throw new Error('Missing relatedId');
      }

      return documentService.listDocuments(
        {
          relatedEntity: currentEntity.value,
          relatedId: id,
          presignedUrls: true,
          expirationInSeconds,
          ...currentPage.value
        },
        { requestConfig: { signal } }
      );
    }
  });

  const refreshedAttachmentIds = new Set<string>();

  /** Re-signs the list after a dead URL, once per attachment — a second failure would loop. */
  function refreshExpiredUrl(attachment: YardTaskAttachment): void {
    if (refreshedAttachmentIds.has(attachment.id)) {
      return;
    }

    refreshedAttachmentIds.add(attachment.id);
    query.refetch();
  }

  return {
    attachments: computed<YardTaskAttachment[]>(() => query.data.value?.items ?? []),
    meta: computed<Maybe<AttachmentListMeta>>(() => query.data.value?.meta ?? null),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    refreshExpiredUrl
  };
}
